import { useEffect, useRef } from 'react'
import type { LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils/cn'

export interface SettingsSection<T extends string> {
  id: T
  label: string
  icon: LucideIcon
  /** Optional group heading rendered above the first item of the group. */
  group?: string
}

export interface SectionNavProps<T extends string> {
  sections: readonly SettingsSection<T>[]
  active: T
  onSelect: (id: T) => void
}

export function SectionNav<T extends string>({
  sections,
  active,
  onSelect,
}: SectionNavProps<T>) {
  const listRef = useRef<HTMLUListElement>(null)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.target instanceof HTMLElement)) return
      if (!listRef.current?.contains(e.target)) return
      const idx = sections.findIndex((s) => s.id === active)
      if (e.key === 'j' || e.key === 'ArrowDown') {
        e.preventDefault()
        const next = sections[Math.min(sections.length - 1, idx + 1)]
        if (next) onSelect(next.id)
      } else if (e.key === 'k' || e.key === 'ArrowUp') {
        e.preventDefault()
        const prev = sections[Math.max(0, idx - 1)]
        if (prev) onSelect(prev.id)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [sections, active, onSelect])

  return (
    <nav
      aria-label="Settings sections"
      className="flex w-[220px] flex-shrink-0 flex-col gap-1 border-r border-border-subtle bg-surface-sunk p-2"
    >
      <ul ref={listRef} className="flex flex-col gap-0.5">
        {sections.map((s, i) => {
          const isActive = s.id === active
          const Icon = s.icon
          const showGroup = !!s.group && s.group !== sections[i - 1]?.group
          return (
            <li key={s.id}>
              {showGroup ? (
                <div className="text-role-section-label px-2.5 pb-1 pt-3 first:pt-1">
                  {s.group}
                </div>
              ) : null}
              <button
                type="button"
                onClick={() => onSelect(s.id)}
                aria-current={isActive ? 'page' : undefined}
                className={cn(
                  'flex w-full cursor-pointer items-center gap-2 rounded-xs border-none bg-transparent px-2.5 py-1.5 text-left text-sm text-secondary hover:bg-surface-hover hover:text-primary',
                  isActive && 'bg-accent-subtle text-accent-text',
                )}
              >
                <Icon size={14} aria-hidden="true" />
                {s.label}
              </button>
            </li>
          )
        })}
      </ul>
    </nav>
  )
}
